import { useMemo, useEffect, useCallback } from 'react';
import _debounce from 'lodash/debounce';
import { helpers } from '@minecraft.utils';
import { SelectOption } from '@minecraft.typings';
import { DropdownWithChildrenProps } from '../types';
import { useDropdownWithChildrenContext } from '../context';
import { getCleanOptions } from '../utils';

type UseSearchOptionsArgs = Pick<
  DropdownWithChildrenProps,
  'withAutocomplete' | 'initialOffset' | 'initialLimit' | 'options' | 'onOptionsFetch'
>;

type UseSearchOptionsResult = {
  visibleOptions: SelectOption[];
};

const { searchOptions } = helpers;

const useSearchOptions = ({
  withAutocomplete,
  initialOffset,
  initialLimit,
  options,
  onOptionsFetch,
}: UseSearchOptionsArgs): UseSearchOptionsResult => {
  const { isDropdownOpen, searchInputValue } = useDropdownWithChildrenContext();

  const handleOptionsFetch = useCallback((): void => {
    onOptionsFetch?.(searchInputValue, initialOffset, initialLimit);
  }, [searchInputValue, onOptionsFetch]);

  const debouncedSearch = useCallback(_debounce(handleOptionsFetch, 1000, { maxWait: 1000 }), [handleOptionsFetch]);

  useEffect(() => {
    if (withAutocomplete && isDropdownOpen && searchInputValue) {
      debouncedSearch();
    }

    if (isDropdownOpen && !searchInputValue) {
      handleOptionsFetch();
    }

    // Cancel the debounce on useEffect cleanup.
    return (): void => {
      debouncedSearch.cancel();
    };
  }, [withAutocomplete, isDropdownOpen, searchInputValue]);

  const visibleOptions = useMemo(() => {
    const cleanOptions = getCleanOptions(options);

    if (!withAutocomplete) {
      return searchOptions(searchInputValue, cleanOptions);
    }

    return cleanOptions;
  }, [withAutocomplete, searchInputValue, options]);

  return useMemo(
    () => ({
      visibleOptions,
    }),
    [visibleOptions]
  );
};

export default useSearchOptions;
